import React, { useState, useEffect, useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
} from "chart.js";

import {
  BarChartSkeleton,
  WorkflowChartSkeleton,
  RunStatusChartSkeleton,
} from "./DevDashboardSkeleton";

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip
);

const PERIOD_OPTIONS = [
  { value: "7", label: "Last 7 Days" },
  { value: "30", label: "Last 30 Days" },
  { value: "90", label: "Last 90 Days" },
];

/**
 * DevAnalyticsPanels
 * Four strictly separated analytics charts:
 * 1. Machine Usage Trend
 * 2. Revenue / Earnings Trend
 * 3. Workflow Usage by Machine
 * 4. Run Success / Failure Trend
 *
 * Each chart maintains its own period filter, x-axis date series, and dataset
 * so changing one chart never overwrites or interferes with the other three.
 */
const DevAnalyticsPanels = ({ data, onPeriodChange, loading = {} }) => {
  const [usagePeriod, setUsagePeriod] = useState(data?.usage?.period || "30");
  const [revenuePeriod, setRevenuePeriod] = useState(
    data?.revenue?.period || "30"
  );
  const [workflowPeriod, setWorkflowPeriod] = useState(
    data?.workflow?.period || "30"
  );
  const [statusPeriod, setStatusPeriod] = useState(
    data?.status?.period || "30"
  );

  useEffect(() => {
    if (data?.usage?.period) setUsagePeriod(String(data.usage.period));
  }, [data?.usage?.period]);

  useEffect(() => {
    if (data?.revenue?.period) setRevenuePeriod(String(data.revenue.period));
  }, [data?.revenue?.period]);

  useEffect(() => {
    if (data?.workflow?.period) setWorkflowPeriod(String(data.workflow.period));
  }, [data?.workflow?.period]);

  useEffect(() => {
    if (data?.status?.period) setStatusPeriod(String(data.status.period));
  }, [data?.status?.period]);

  const getPeriodLabel = (val) =>
    PERIOD_OPTIONS.find((opt) => opt.value === String(val))?.label ||
    `Last ${val} Days`;

  const usagePeriodLabel =
    data?.usage?.periodLabel || getPeriodLabel(usagePeriod);
  const revenuePeriodLabel =
    data?.revenue?.periodLabel || getPeriodLabel(revenuePeriod);
  const statusPeriodLabel =
    data?.status?.periodLabel || getPeriodLabel(statusPeriod);

  // 1. Machine Usage Trend data
  const usageDaysLabels = data?.usage?.daysLabels || data?.daysLabels || [];
  const usageFeaturedTicks =
    data?.usage?.featuredTicks || data?.featuredTicks || [];
  const usageRuns = data?.usage?.runs || data?.machineUsage?.runs || [];

  // 2. Revenue Trend data
  const revenueDaysLabels = data?.revenue?.daysLabels || data?.daysLabels || [];
  const revenueFeaturedTicks =
    data?.revenue?.featuredTicks || data?.featuredTicks || [];
  const revenueValues = data?.revenue?.values || data?.revenue?.values || [];

  // 3. Workflow Usage data (filter out 0 machine usage)
  const workflowItems = useMemo(() => {
    const items = data?.workflow?.items || data?.workflowUsage || [];
    return items.filter((item) => Number(item.count ?? item.runs ?? 0) > 0);
  }, [data?.workflow?.items, data?.workflowUsage]);

  // 4. Run Success / Failure Trend data
  const statusDaysLabels = data?.status?.daysLabels || data?.daysLabels || [];
  const statusFeaturedTicks =
    data?.status?.featuredTicks || data?.featuredTicks || [];
  const statusSuccessful =
    data?.status?.successful || data?.runSuccessFailure?.successful || [];
  const statusFailed =
    data?.status?.failed || data?.runSuccessFailure?.failed || [];

  // Independent Max calculation per chart
  const maxRuns = useMemo(() => {
    const highest = usageRuns.length ? Math.max(...usageRuns) : 0;
    return highest > 40 ? Math.ceil((highest + 10) / 10) * 10 : 40;
  }, [usageRuns]);

  const revenueScale = useMemo(() => {
    const validValues = (revenueValues || []).map((v) => Number(v || 0));
    const highest = validValues.length ? Math.max(...validValues) : 0;

    if (!highest || highest <= 0) {
      return { max: 1, stepSize: 0.25 };
    }

    // Add ~20% headroom margin so the peak doesn't touch the top border
    const targetMax = highest * 1.2;
    const exponent = Math.floor(Math.log10(targetMax));
    const fraction = targetMax / Math.pow(10, exponent);

    let niceFraction;
    let steps;
    if (fraction <= 1.2) {
      niceFraction = 1.2;
      steps = 4;
    } else if (fraction <= 1.5) {
      niceFraction = 1.5;
      steps = 3;
    } else if (fraction <= 2) {
      niceFraction = 2;
      steps = 4;
    } else if (fraction <= 2.5) {
      niceFraction = 2.5;
      steps = 5;
    } else if (fraction <= 3) {
      niceFraction = 3;
      steps = 3;
    } else if (fraction <= 4) {
      niceFraction = 4;
      steps = 4;
    } else if (fraction <= 5) {
      niceFraction = 5;
      steps = 5;
    } else if (fraction <= 6) {
      niceFraction = 6;
      steps = 3;
    } else if (fraction <= 8) {
      niceFraction = 8;
      steps = 4;
    } else {
      niceFraction = 10;
      steps = 5;
    }

    const niceMax = niceFraction * Math.pow(10, exponent);
    const stepSize = niceMax / steps;
    return {
      max: Number(niceMax.toFixed(4)),
      stepSize: Number(stepSize.toFixed(4)),
    };
  }, [revenueValues]);

  const maxStatus = useMemo(() => {
    const sums = statusSuccessful.map(
      (s, i) => Number(s || 0) + Number(statusFailed[i] || 0)
    );
    const highest = sums.length ? Math.max(...sums) : 0;
    return highest > 40 ? Math.ceil((highest + 10) / 10) * 10 : 40;
  }, [statusSuccessful, statusFailed]);

  const maxWorkflowVal = useMemo(() => {
    const counts = workflowItems.map((i) => Number(i.count || 0));
    const highest = counts.length ? Math.max(...counts) : 0;
    return highest > 0 ? highest : 1;
  }, [workflowItems]);

  // Dynamic X-axis configuration per chart
  const makeXAxis = (ticksList) => ({
    grid: {
      display: false,
      drawBorder: false,
    },
    ticks: {
      callback: function (val) {
        const label = this.getLabelForValue(val);
        return (ticksList || []).includes(label) ? label : "";
      },
      autoSkip: false,
      maxRotation: 0,
      color: "#94a3b8",
      font: {
        family: "'Sora', sans-serif",
        size: 9.5,
      },
    },
  });

  // 1. Machine Usage Chart Config
  const usageChartData = {
    labels: usageDaysLabels,
    datasets: [
      {
        label: "Runs",
        data: usageRuns,
        backgroundColor: "#60a5fa",
        borderRadius: 2,
        maxBarThickness: 7.5,
      },
    ],
  };

  const usageChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleFont: { family: "'Sora', sans-serif", size: 11 },
        bodyFont: { family: "'Sora', sans-serif", size: 11 },
        padding: 8,
        cornerRadius: 4,
      },
    },
    scales: {
      x: makeXAxis(usageFeaturedTicks),
      y: {
        min: 0,
        max: maxRuns,
        ticks: {
          stepSize: Math.ceil(maxRuns / 4),
          color: "#94a3b8",
          font: { family: "'Sora', sans-serif", size: 9.5 },
        },
        grid: {
          color: "#f1f5f9",
          drawBorder: false,
        },
      },
    },
  };

  // 2. Revenue Trend Chart Config
  const revenueChartData = {
    labels: revenueDaysLabels,
    datasets: [
      {
        label: "Revenue (USD)",
        data: revenueValues,
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.08)",
        fill: true,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 2.8,
        pointHoverRadius: 4.5,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 1.5,
      },
    ],
  };

  const revenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleFont: { family: "'Sora', sans-serif", size: 11 },
        bodyFont: { family: "'Sora', sans-serif", size: 11 },
        padding: 8,
        cornerRadius: 4,
        callbacks: {
          label: (context) => ` Revenue: $${Number(context.raw).toFixed(2)}`,
        },
      },
    },
    scales: {
      x: makeXAxis(revenueFeaturedTicks),
      y: {
        min: 0,
        max: revenueScale.max,
        ticks: {
          stepSize: revenueScale.stepSize,
          callback: (val) => {
            const num = Number(Number(val).toFixed(4));
            if (num === 0) return "$0";
            return `$${num % 1 === 0 ? num : Number(num.toFixed(2))}`;
          },
          color: "#94a3b8",
          font: { family: "'Sora', sans-serif", size: 9.5 },
        },
        grid: {
          color: "#f1f5f9",
          drawBorder: false,
        },
      },
    },
  };

  // 4. Run Success / Failure Trend Chart Config
  const statusChartData = {
    labels: statusDaysLabels,
    datasets: [
      {
        label: "Successful",
        data: statusSuccessful,
        backgroundColor: "#10b981",
        stack: "runs",
        maxBarThickness: 7.5,
      },
      {
        label: "Failed",
        data: statusFailed,
        backgroundColor: "#f43f5e",
        stack: "runs",
        borderRadius: { topLeft: 2, topRight: 2 },
        maxBarThickness: 7.5,
      },
    ],
  };

  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleFont: { family: "'Sora', sans-serif", size: 11 },
        bodyFont: { family: "'Sora', sans-serif", size: 11 },
        padding: 8,
        cornerRadius: 4,
      },
    },
    scales: {
      x: makeXAxis(statusFeaturedTicks),
      y: {
        stacked: true,
        min: 0,
        max: maxStatus,
        ticks: {
          stepSize: Math.ceil(maxStatus / 4),
          color: "#94a3b8",
          font: { family: "'Sora', sans-serif", size: 9.5 },
        },
        grid: {
          color: "#f1f5f9",
          drawBorder: false,
        },
      },
    },
  };

  return (
    <div className="dev-analytics-grid">
      {/* Panel 1: Machine Usage Trend */}
      {loading?.usage ? (
        <BarChartSkeleton
          titleW="130px"
          barHeights={[45, 75, 35, 85, 60, 50, 90, 40]}
        />
      ) : (
        <div className="dev-analytics-card">
          <div className="dev-analytics-header">
            <div className="dev-analytics-title">
              Machine Usage Trend{" "}
              <span className="dev-analytics-period-hint">
                ({usagePeriodLabel})
              </span>
            </div>
            <select
              className="dev-period-select"
              value={usagePeriod}
              onChange={(e) => {
                const val = e.target.value;
                setUsagePeriod(val);
                if (onPeriodChange) onPeriodChange("usage", val);
              }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="dev-chart-wrapper">
            <Bar data={usageChartData} options={usageChartOptions} />
          </div>

          <div className="dev-chart-legend">
            <span className="dev-legend-item">
              <span
                className="dev-legend-dot"
                style={{ backgroundColor: "#60a5fa" }}
              />
              Runs
            </span>
          </div>
        </div>
      )}

      {/* Panel 2: Revenue / Earnings Trend */}
      {loading?.revenue ? (
        <BarChartSkeleton
          titleW="140px"
          barHeights={[35, 60, 45, 80, 55, 70, 90, 65]}
        />
      ) : (
        <div className="dev-analytics-card">
          <div className="dev-analytics-header">
            <div className="dev-analytics-title">
              Revenue / Earnings Trend{" "}
              <span className="dev-analytics-period-hint">
                ({revenuePeriodLabel})
              </span>
            </div>
            <select
              className="dev-period-select"
              value={revenuePeriod}
              onChange={(e) => {
                const val = e.target.value;
                setRevenuePeriod(val);
                if (onPeriodChange) onPeriodChange("revenue", val);
              }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="dev-chart-wrapper">
            <Line data={revenueChartData} options={revenueChartOptions} />
          </div>

          <div className="dev-chart-legend">
            <span className="dev-legend-item">
              <span
                className="dev-legend-dot"
                style={{ backgroundColor: "#10b981" }}
              />
              Revenue (USD)
            </span>
          </div>
        </div>
      )}

      {/* Panel 3: Workflow Usage by Machine */}
      {loading?.workflow ? (
        <WorkflowChartSkeleton />
      ) : (
        <div className="dev-analytics-card">
          <div className="dev-analytics-header">
            <div className="dev-analytics-title">Workflow Usage by Machine</div>
            <select
              className="dev-period-select"
              value={workflowPeriod}
              onChange={(e) => {
                const val = e.target.value;
                setWorkflowPeriod(val);
                if (onPeriodChange) onPeriodChange("workflow", val);
              }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="dev-workflow-bars-wrapper">
            {workflowItems.length > 0 ? (
              workflowItems.map((item, idx) => {
                const barPct = Math.round((item.count / maxWorkflowVal) * 100);
                return (
                  <div key={idx} className="dev-workflow-bar-row">
                    <span className="dev-workflow-name" title={item.machineName}>
                      {item.machineName}
                    </span>
                    <div className="dev-workflow-track">
                      <div
                        className="dev-workflow-fill"
                        style={{
                          width: `${barPct}%`,
                          backgroundColor: item.color,
                        }}
                      />
                    </div>
                    <span className="dev-workflow-count">{item.count}</span>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "#94a3b8",
                  fontSize: "12px",
                }}
              >
                No workflow usage data
              </div>
            )}
          </div>

          <div className="dev-chart-legend dev-chart-legend-placeholder">
            {/* Invisible spacer to align card height cleanly */}
            <span style={{ opacity: 0 }}>&nbsp;</span>
          </div>
        </div>
      )}

      {/* Panel 4: Run Success / Failure Trend */}
      {loading?.status ? (
        <RunStatusChartSkeleton />
      ) : (
        <div className="dev-analytics-card">
          <div className="dev-analytics-header">
            <div className="dev-analytics-title">
              Run Success / Failure Trend{" "}
              <span className="dev-analytics-period-hint">
                ({statusPeriodLabel})
              </span>
            </div>
            <select
              className="dev-period-select"
              value={statusPeriod}
              onChange={(e) => {
                const val = e.target.value;
                setStatusPeriod(val);
                if (onPeriodChange) onPeriodChange("status", val);
              }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="dev-chart-wrapper">
            <Bar data={statusChartData} options={statusChartOptions} />
          </div>

          <div className="dev-chart-legend">
            <span className="dev-legend-item">
              <span
                className="dev-legend-dot"
                style={{ backgroundColor: "#10b981" }}
              />
              Successful
            </span>
            <span className="dev-legend-item">
              <span
                className="dev-legend-dot"
                style={{ backgroundColor: "#f43f5e" }}
              />
              Failed
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevAnalyticsPanels;
